import type { GameEvent, GameView } from "@numbet/engine";
import { formatChips, formatWithUnit } from "../format.ts";
import { t } from "../i18n.ts";

export interface LogLine {
  id: number;
  text: string;
  /** Hervorgehobene Zeilen (Hinweise, Antwort, Gewinner). */
  highlight?: boolean;
}

let nextId = 1;

/** Übersetzt Ereignisse in Zeilen für den Verlauf. Namen und Antwort kommen aus der aktuellen Sicht. */
export function eventsToLines(events: GameEvent[], view: GameView): LogLine[] {
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? "?";
  const lines: LogLine[] = [];
  const push = (text: string, highlight = false) => lines.push({ id: nextId++, text, highlight });

  for (const e of events) {
    switch (e.type) {
      case "playerJoined":
        push(t.ev.joined(name(e.playerId)));
        break;
      case "playerLeft":
        push(t.ev.left(name(e.playerId)));
        break;
      case "handStarted":
        push(t.ev.handStarted(e.handNumber), true);
        break;
      case "blindPosted":
        push(t.ev.blind(name(e.playerId), formatChips(e.amount)));
        break;
      case "estimateSubmitted":
        push(t.ev.estimated(name(e.playerId)));
        break;
      case "playerActed": {
        const who = name(e.playerId);
        const amount = formatChips(e.roundBet);
        const text = {
          fold: t.ev.fold(who),
          check: t.ev.check(who),
          call: t.ev.call(who, amount),
          bet: t.ev.bet(who, amount),
          raise: t.ev.raise(who, amount),
          allIn: t.ev.allIn(who, amount),
        }[e.move];
        push(text);
        break;
      }
      case "playerTimedOut":
        push(t.ev.timedOut(name(e.playerId)));
        break;
      case "hintRevealed":
        push(t.ev.hint(e.index + 1), true);
        break;
      case "answerRevealed": {
        const q = view.hand?.question;
        const answer = view.hand?.answer;
        if (q && answer !== null && answer !== undefined) push(t.ev.answer(formatWithUnit(answer, q.unit, q.decimals)), true);
        break;
      }
      case "uncalledBetReturned":
        push(t.ev.returned(name(e.playerId), formatChips(e.amount)));
        break;
      case "showdown":
        push(t.ev.showdown, true);
        break;
      case "potAwarded":
        push(
          e.winners.length === 1
            ? t.winsPot(name(e.winners[0] ?? ""), formatChips(e.amount))
            : t.splitPot(e.winners.map(name).join(", "), formatChips(e.amount)),
          true,
        );
        break;
      case "estimateShown":
        push(t.ev.shown(name(e.playerId)));
        break;
      case "playerSatOut":
        push(t.ev.satOut(name(e.playerId)));
        break;
      case "playerSatIn":
        push(t.ev.satIn(name(e.playerId)));
        break;
      case "playerEliminated":
        push(t.ev.eliminated(name(e.playerId)));
        break;
      default:
        break;
    }
  }
  return lines;
}
